import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';

// Models
import { InvoicesData, InvoicesCallsData } from 'src/app/modules/invoices/models/invoices';
import { ScreenLoaderData } from 'src/app/platform/components/ui-screen-loader/ui-screen-loader.model';

// Services
import { UiService } from 'src/app/platform/services/ui.service';
import { InvoicesService } from 'src/app/modules/invoices/invoices.service';
import { PlatformService, ListExportService } from 'src/app/platform/services/platform.service';
import { MetaData } from 'src/app/platform/models/utils';

@Component({
  selector: 'app-invoices-calls-list',
  templateUrl: './invoices-calls-list.component.html',
  styleUrls: ['./invoices-calls-list.component.scss']
})
export class InvoicesCallsListComponent implements OnInit {
  public invoice: InvoicesData;
  private invoiceId: any;
  private invoiceType: any;
  public filters: any = {
    type: [],
    originNumber: '',
    destinationNumber: '',
    destinationName: '',
    date: { start: '', end: ''},
  };
  public filterTypes: MetaData[] = [];
  public filterNumbers: MetaData[] = [];
  public calls: InvoicesCallsData[] = [];
  public callsTotal = 0;
  public callsPage = 1;
  public callsPageSize = 25;
  public callsUpdating = false;
  public callsInitStatus = false;
  public errors = {
    load: new ScreenLoaderData('Cargando llamadas', 'Estamos buscando todas las llamadas de la factura seleccionada.', 'phone_in_talk', true),
    empty: new ScreenLoaderData('No existen llamadas facturadas', 'Esta factura no dispone de llamadas realizadas.', 'help_outline'),
    error: new ScreenLoaderData('Ocurrió un error al intentar cargar el listado de llamadas', 'No pudimos obtener las llamadas, estamos trabajando para solucionar este problema. Por favor intenta revisar nuevamente mas tarde.', 'highlight_off'),
  };

  constructor(
    private uiService: UiService,
    private platformService: PlatformService,
    private listExportService: ListExportService,
    private invoicesService: InvoicesService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.invoiceId = this.route.snapshot.paramMap.get('invoiceId');
    this.invoiceType = this.route.snapshot.paramMap.get('invoiceType');

    this.uiService.screenLoaderSet(this.errors.load);

    this.invoicesService.invoiceGet(this.invoiceId, this.invoiceType).then((invoice) => {
      this.invoice = invoice.result;

      this.callsLoad(this.callsPage, this.callsPageSize).then((success) => {
        if (success.registersTotal > 0) {
          this.callsInitStatus = true;
          this.calls = success.registers;
          this.callsTotal = success.registersTotal;
          this.callsPage = success.page;
        } else {
          this.uiService.screenLoaderSet(this.errors.empty);
        }
      }).catch((error) => {
        this.uiService.screenLoaderSet(this.errors.error);
      });

      this.invoicesService.callsTypesGet(this.invoiceId, this.invoiceType).then((success) => {
        this.filterTypes = success.result;
      }).catch((error) => {
        this.filterTypes = [new MetaData(0, 'Filtro no disponible')];
      });

      this.invoicesService.callsNumbersGet(this.invoiceId, this.invoiceType).then((success) => {
        this.filterNumbers = success.result;
      }).catch((error) => {
        this.filterNumbers = [new MetaData(0, 'Filtro no disponible')];
      });
    }).catch(() => {
      this.uiService.screenLoaderSet(this.errors.error);
    });
  }

  private callsLoad(page: number | null = null, limit: number | null = null, filters: any | null = null): Promise<any> {
    return new Promise((resolve, reject) => {
      this.invoicesService.callsGet(this.invoiceId, this.invoiceType, page, limit, filters).then((success) => {
        resolve(success.result);
      }).catch(() => {
        reject(false);
      });
    });
  }

  private callsChange(): void {
    this.callsUpdating = true;

    this.callsLoad(this.callsPage, this.callsPageSize, this.filters).then((success) => {
      this.callsUpdating = false;

      if (success.registersTotal > 0) {
        this.calls = success.registers;
        this.callsTotal = success.registersTotal;
        this.callsPage = success.page;
      } else {
        this.calls = [];
      }
    }).catch((error) => {
      this.callsUpdating = false;
      this.calls = [];
      this.uiService.showSnackBar(this.errors.error.message);
    });
  }

  onFilterSelect(filter: string, value?: any): void {
    if (typeof this.filters[filter] !== 'undefined') {
      let update = true;

      if (filter === 'date') {
        if (this.filters[filter].end === '' || this.filters[filter].end === null) {
          update = false;
        }

        if (value === '') {
          this.filters[filter] = {start: '', end: ''};
          update = true;
        }
      } else {
        this.filters[filter] = value;
      }

      if (update) {
        this.callsPage = 1;

        this.callsChange();
      }
    }
  }

  onPaginationChange(event: PageEvent): void {
    this.callsPage = event.pageIndex + 1;
    this.callsPageSize = event.pageSize;

    this.callsChange();
  }

  onBack(): void {
    this.platformService.onBack();
  }

  onDownload(type: string): void {
    this.uiService.showSpinner(true);

    this.callsLoad(1, null, this.filters).then((success) => {
      if (success.registersTotal > 0) {
        const items: any = [];

        success.registers.forEach((item: any) => {
          items.push({
            fecha: item.date,
            numeroOrigen: item.originNumber,
            numeroDestino: item.destinationNumber,
            destino: item.destination,
            minutos: item.minutes,
            importe: item.amount,
            total: item.total,
          });
        });

        if (items.length > 0) {
          this.uiService.showSpinner(true);

          const structure = [
            {header: 'Fecha', body : 'fecha'},
            {header: 'Número de origen', body : 'numeroOrigen'},
            {header: 'Número de destino', body : 'numeroDestino'},
            {header: 'Destino', body : 'destino'},
            {header: 'Minutos', body : 'minutos'},
            {header: 'Importe', body : 'importe'},
            {header: 'Total', body : 'total'}
          ];

          this.listExportService.downloadFile(type, 'Listado de llamadas - Factura ' + this.invoice.receipt, structure, items).then((response) => {
            this.uiService.showSpinner(false);
          });
        }
      } else {
        this.uiService.showSpinner(false);
        this.uiService.showSnackBar('No existen registros para generar la exportación');
      }
    }).catch((error) => {
      this.uiService.showSpinner(false);
      this.uiService.dialogShow('Ups!', 'No es posible generar la exportación en este momento. Estamos trabajando para solucionar el inconveniente.', 'alert');
    });
  }
}
